import { call, put, all, select } from 'redux-saga/effects';
import { types as projectsTypes } from '../reducers/projects';
import * as Api from '../utils/Api';
import { Type as accountTypes } from '../reducers/Account';
import history from '../history';
import {
  AUTO_REVIEW_UPDATE_FAILURE,
  AUTO_REVIEW_UPDATE_SUCCESS,
  REVIEWER_APPROVALS_UPDATE_FAILURE,
  REVIEWER_APPROVALS_UPDATE_SUCCESS,
  logEvent,
} from '../utils/analytics';
import URI from 'urijs';
import range from 'lodash/range';
import { Type } from '../reducers/Navigation';
import { isProjectLevelSelector } from '../selectors/Router';
import { ownerProjectsSelector, projectSelector } from '../selectors/Projects';

const PARALLEL_PAGES = 2;

export function* fetchProjects(action) {
  const { providerId, organizationSlug, sortBy } = action;
  if (!providerId || !organizationSlug) {
    throw new Error('Missing required parameters for fetching projects');
  }
  const getUrl = (page) => {
    const href = URI(
      `organizations/${action.providerId}/${action.organizationSlug}/projects`
    );
    if (sortBy && sortBy !== 'last_activity_at') {
      href.addQuery({ sort: sortBy });
    }
    return href.addQuery({ page }).toString();
  };

  let projects = [];

  let pages = range(1, PARALLEL_PAGES + 1);

  while (true) {
    const responses = yield all(
      pages.map((page) => {
        const url = getUrl(page);
        return call(Api.get, url);
      })
    );

    for (const response of responses) {
      if (!response.success) {
        yield put({
          type: projectsTypes.PROJECTS_FAILURE,
          error: response.data ? response.data.error_message : 'Unknown error',
        });
        return;
      }
      // add this page of data
      projects = [...projects, ...response.data.data];
    }

    // increment the pages
    pages = pages.map((page) => page + PARALLEL_PAGES);

    // if the last page doesn't have a next_page_url then we're done
    if (!responses[responses.length - 1].data.next_page_url) {
      // no more pages
      break;
    }
  }

  yield put({
    type: projectsTypes.PROJECTS_SUCCESS,
    organizationSlug,
    projects,
    providerId,
    key: sortBy || 'last_activity_at',
  });
}

export function* navigateAfterProjectsFetch() {
  const isProjectLevel = yield select(isProjectLevelSelector);
  const currentProjectSet = yield select(projectSelector);
  const fetchedProjects = yield select(ownerProjectsSelector);
  if (isProjectLevel && !currentProjectSet && fetchedProjects?.length) {
    yield put({ type: Type.NAVIGATE, href: '/dash' });
  }
}

export function* fetchProject(action) {
  const { projectUUID } = action;
  if (!projectUUID) {
    throw new Error('Missing required parameters for fetching a project');
  }
  const response = yield call(Api.get, `projects/${projectUUID}`);
  if (!response.success) {
    yield put({
      type: projectsTypes.PROJECT_FAILURE,
      error: response.data ? response.data.error_message : 'Unknown error',
    });
  } else {
    yield put({
      type: projectsTypes.PROJECT_SUCCESS,
      projectUUID,
      project: response.data,
    });
  }
}

export function* toggleApprovalsAllowed(action) {
  const { organization, projectUUID, enabled } = action;
  if (!organization || !projectUUID || enabled === undefined) {
    throw new Error(
      'Missing required parameters for toggling reviewer approvals'
    );
  }
  const response = yield call(
    Api.put,
    `projects/${projectUUID}/reviewer-approvals`,
    { enabled }
  );
  if (!response.success) {
    logEvent(REVIEWER_APPROVALS_UPDATE_FAILURE);
    yield put({
      type: projectsTypes.TOGGLE_APPROVALS_FAILURE,
      error: response.data ? response.data.error_message : 'Unknown error',
      errorCode: response.status,
    });
  } else {
    logEvent(REVIEWER_APPROVALS_UPDATE_SUCCESS);
    yield fetchProjects({
      providerId: organization.providerId,
      organizationSlug: organization.slug,
    });
    yield put({
      type: projectsTypes.TOGGLE_APPROVALS_SUCCESS,
    });
  }
}

export function* toggleAutoReview(action) {
  const { organization, projectUUID, enabled } = action;
  if (!organization || !projectUUID || enabled === undefined) {
    throw new Error('Missing required parameters for toggling auto review');
  }
  const response = yield call(
    Api.post,
    `projects/${projectUUID}/toggle-auto-review`,
    { enabled }
  );
  if (!response.success) {
    logEvent(AUTO_REVIEW_UPDATE_FAILURE);
    yield put({
      type: projectsTypes.TOGGLE_AUTO_REVIEW_FAILURE,
      error: response.data ? response.data.error_message : 'Unknown error',
      errorCode: response.status,
    });
  } else {
    logEvent(AUTO_REVIEW_UPDATE_SUCCESS);
    yield fetchProjects({
      providerId: organization.providerId,
      organizationSlug: organization.slug,
    });
    yield put({
      type: projectsTypes.TOGGLE_AUTO_REVIEW_SUCCESS,
    });
  }
}

export function* bulkToggleAutoReview(action) {
  const { organization, enabled } = action;
  if (!organization) {
    throw new Error(
      'Missing required parameters for bulk-toggling auto review'
    );
  }
  const response = yield call(
    Api.put,
    `organizations/${organization.uuid}/projects/auto-review`,
    { organizationUUID: organization.uuid, enabled }
  );
  if (!response.success) {
    yield put({
      type: projectsTypes.BULK_TOGGLE_REVIEW_FAILURE,
      error: response.data ? response.data.error_message : 'Unknown error',
      errorCode: response.status,
    });
  } else {
    yield call(fetchProjects, {
      providerId: organization.providerId,
      organizationSlug: organization.slug,
    });
    yield put({
      type: projectsTypes.BULK_TOGGLE_REVIEW_SUCCESS,
    });
  }
}

export function* toggleAutomationResults(action) {
  const { projectUUID, enabled } = action;
  if (!projectUUID || enabled === undefined) {
    throw new Error(
      'Missing required parameters for toggling automation results'
    );
  }

  const response = yield call(
    Api.put,
    `projects/${projectUUID}/static-enabled`,
    { enabled }
  );

  if (!response.success) {
    yield put({
      type: projectsTypes.TOGGLE_AUTOMATION_FAILURE,
      error: response.data ? response.data.error_message : 'Unknown error',
      errorCode: response.status,
    });
  } else {
    yield fetchProject({ projectUUID });
    yield put({
      type: projectsTypes.TOGGLE_AUTOMATION_SUCCESS,
    });
  }
}

export function* markProjectOnboarded(action) {
  const { projectUUID, isOrgOnboarded, url } = action;
  if (!projectUUID) {
    throw new Error(
      'Missing required parameters for marking project as onboarded'
    );
  }
  const response = yield call(
    Api.post,
    `projects/${projectUUID}/mark-onboarding-complete`
  );
  if (!response.success) {
    yield put({
      type: projectsTypes.MARK_PROJECT_ONBOARDED_FAILURE,
      error: response.data ? response.data.error_message : 'Unknown error',
      errorCode: response.status,
    });
  } else {
    yield fetchProject({ projectUUID });
    //refetches account to update is_onboarded prop for the organization once one of the projects gets onboarded
    if (!isOrgOnboarded) {
      yield put({ type: accountTypes.FETCH_ACCOUNT_REQUEST });
    }
    yield put({
      type: projectsTypes.MARK_PROJECT_ONBOARDED_SUCCESS,
    });
    history.push(url);
  }
}

export function* updateProjectBillingPlan(action) {
  const { plan, projectUUID } = action;
  if (!plan) {
    throw new Error('Missing required parameters for updating billing plan');
  }
  const response = yield call(
    Api.put,
    `projects/${projectUUID}/update-billing-plan`,
    {
      plan,
      projectUUID,
    }
  );
  const data = response.data;
  if (!response.success) {
    yield put({
      type: projectsTypes.UPDATE_BILLING_PLAN_FAILURE,
      error: data ? data.error_message : 'Unknown error',
    });
  } else {
    yield put({
      type: projectsTypes.UPDATE_BILLING_PLAN_SUCCESS,
    });
    yield fetchProject({ projectUUID });
  }
}

export function* updateProjectNotes(action) {
  const { notes, projectUUID } = action;
  if (!notes || !projectUUID) {
    throw new Error('Missing required parameters for setting project notes');
  }
  const response = yield call(
    Api.post,
    `projects/${projectUUID}/notes-from-organization`,
    { notes }
  );
  const data = response.data;
  if (!response.success) {
    yield put({
      type: projectsTypes.UPDATE_PROJECT_NOTES_FAILURE,
      error: data ? data.error_message : 'Unknown error',
    });
  } else {
    yield put({
      type: projectsTypes.UPDATE_PROJECT_NOTES_SUCCESS,
    });
    yield fetchProject({ projectUUID });
  }
}
